"use client";

import { Star } from "lucide-react";
import { useFavorieten } from "@/components/favorieten";
import { cn } from "@/lib/utils";
import type { Favoriet, Sectie } from "@/lib/favorieten";

// De ster waarmee je iets volgt. Overal dezelfde knop, zodat volgen zich
// overal hetzelfde gedraagt - of het nu om een team, een wedstrijd of een
// klasse gaat.
//
// Twee verschijningsvormen. Op een profiel is de ster een van de weinige
// knoppen op het scherm en mag hij als knop herkenbaar zijn - rond, met rand.
// In een lijst van dertig rijen zouden dertig van die rondjes de uitslag zelf
// wegdrukken; daar is het een kale ster die pas opvalt als hij aan staat.

type Vorm = {
  groot?: boolean;
  /** Kale variant voor in een rij. */
  lijst?: boolean;
  /**
   * Nog iets smaller, voor de live-lijst: die heeft naast de naam ook nog twee
   * kolommen met afstanden, en op een telefoon is elke pixel er een.
   */
  smal?: boolean;
};

// Teams zijn er zes plekken in de app, en die geven sectie/id/naam los mee.
// De andere soorten geven een hele favoriet mee. Beide vormen komen hier
// samen zodat er maar één ster bestaat.
type Props = Vorm &
  ({ favoriet: Favoriet } | { sectie: Sectie; id: number; naam: string });

export function FavorietKnop(props: Props) {
  const favoriet: Favoriet =
    "favoriet" in props
      ? props.favoriet
      : { soort: "team", sectie: props.sectie, id: props.id, naam: props.naam };

  const { isFavoriet, wissel, geladen } = useFavorieten();
  const aan = isFavoriet(favoriet);
  const label = aan ? `${favoriet.naam} niet meer volgen` : `${favoriet.naam} volgen`;

  if (props.lijst) {
    return (
      <button
        type="button"
        onClick={() => wissel(favoriet)}
        aria-pressed={aan}
        aria-label={label}
        title={label}
        // Groter dan de ster zelf: op een telefoon moet dit te raken zijn
        // zonder per ongeluk de rij eronder te openen.
        className={cn(
          "flex h-10 shrink-0 items-center justify-center rounded-lg transition-colors",
          props.smal ? "w-9" : "w-10",
          aan ? "text-primary" : "text-white/20 hover:text-white/60",
          !geladen && "opacity-0",
        )}
      >
        <Star className="size-[18px]" fill={aan ? "currentColor" : "none"} />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => wissel(favoriet)}
      aria-pressed={aan}
      aria-label={label}
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full border-2 transition-colors",
        props.groot ? "size-11" : "size-9",
        aan
          ? "border-primary bg-primary text-primary-foreground"
          : "border-white/25 text-white/60 hover:border-white/50 hover:text-white",
        // Tot de opslag gelezen is nog niets uitgesproken tonen.
        !geladen && "opacity-0",
      )}
    >
      <Star className={props.groot ? "size-5" : "size-4"} fill={aan ? "currentColor" : "none"} />
    </button>
  );
}
